import { Box } from "@mui/material";
import React from "react";

/** A framed illustration: purely decorative, so hidden from assistive technology. */
const Illustration = ({
  width,
  children,
}: {
  width: number;
  children: React.ReactNode;
}) => (
  <Box
    component="svg"
    aria-hidden
    focusable="false"
    viewBox="0 0 480 260"
    fill="none"
    sx={{ width: "100%", maxWidth: width, height: "auto", display: "block" }}
  >
    {children}
  </Box>
);

/** The intro's "account created" picture (v220 design): a company card with a green check, 440px wide. */
export const OnboardingIntroIllustration = () => (
  <Illustration width={440}>
    <g fill="#DCE5F6">
      <circle cx="34" cy="58" r="6" />
      <circle cx="446" cy="214" r="7" />
      <circle cx="398" cy="36" r="4" />
      <circle cx="70" cy="226" r="4" />
    </g>
    <g fill="#1E3A78">
      <circle cx="58" cy="130" r="3.5" />
      <circle cx="426" cy="124" r="3.5" />
      <circle cx="118" cy="26" r="3" />
      <circle cx="360" cy="246" r="3" />
    </g>
    <g stroke="#1B7A5A" strokeWidth="3" strokeLinecap="round">
      <path d="M86 40l10-10" />
      <path d="M402 72l10 10" />
      <path d="M40 190l-10 10" />
      <path d="M434 180l12 0" />
    </g>
    <g stroke="#CBD2E0" strokeWidth="3" strokeLinecap="round">
      <path d="M150 14l0 12" />
      <path d="M330 20l12 0" />
      <path d="M100 240l12 0" />
    </g>
    <rect
      x="112"
      y="56"
      width="256"
      height="150"
      rx="16"
      fill="#FFFFFF"
      stroke="#CBD2E0"
      strokeWidth="1.5"
    />
    <rect x="136" y="82" width="48" height="48" rx="11" fill="#033099" />
    <g transform="translate(148.55 92) scale(1.0669)">
      <path
        d="M15.796 26.2254L15.5715 14.4672L20.3481 8.59748L20.7779 26.2442L15.796 26.2254Z"
        fill="#FFFFFF"
      />
      <path
        d="M4.96759 26.2254L5.07267 20.6097L0.429887 15.5631L0 26.2254H4.96759Z"
        fill="#FFFFFF"
      />
      <path
        d="M6.03733 11.8523L0.329384 6.15659V12.6989L5.85105 18.578C6.07267 18.7995 6.33811 18.9738 6.63094 19.0902C6.92376 19.2066 7.23774 19.2626 7.55342 19.2546C7.8691 19.2467 8.17977 19.175 8.46618 19.0441C8.75259 18.9131 9.00865 18.7256 9.2185 18.4933L21.2888 4.23295L21.4416 0L9.20417 11.9604C8.76262 12.3451 8.18804 12.5483 7.59885 12.5282C7.00965 12.508 6.45075 12.2661 6.03733 11.8523V11.8523Z"
        fill="#FA6450"
      />
    </g>
    <rect x="200" y="90" width="124" height="12" rx="6" fill="#1A2340" />
    <rect x="200" y="112" width="76" height="10" rx="5" fill="#CBD2E0" />
    <rect x="136" y="154" width="120" height="10" rx="5" fill="#EEF2FA" />
    <rect x="136" y="174" width="180" height="10" rx="5" fill="#EEF2FA" />
    <circle
      cx="366"
      cy="60"
      r="32"
      fill="#1B7A5A"
      stroke="#FFFFFF"
      strokeWidth="6"
    />
    <path
      d="M352 61l9 9 19-19"
      stroke="#FFFFFF"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Illustration>
);

/** The done card's picture (v220 design): a card of list tiles and a button, with a green check, 360px wide. */
export const OnboardingDoneIllustration = () => (
  <Illustration width={360}>
    <g fill="#DCE5F6">
      <circle cx="34" cy="58" r="6" />
      <circle cx="446" cy="214" r="7" />
      <circle cx="398" cy="36" r="4" />
      <circle cx="70" cy="226" r="4" />
    </g>
    <g fill="#1E3A78">
      <circle cx="58" cy="130" r="3.5" />
      <circle cx="426" cy="124" r="3.5" />
      <circle cx="118" cy="26" r="3" />
      <circle cx="360" cy="246" r="3" />
    </g>
    <g stroke="#1B7A5A" strokeWidth="3" strokeLinecap="round">
      <path d="M86 40l10-10" />
      <path d="M402 72l10 10" />
      <path d="M40 190l-10 10" />
      <path d="M434 180l12 0" />
    </g>
    <g stroke="#CBD2E0" strokeWidth="3" strokeLinecap="round">
      <path d="M150 14l0 12" />
      <path d="M330 20l12 0" />
      <path d="M100 240l12 0" />
    </g>
    <rect
      x="112"
      y="56"
      width="256"
      height="150"
      rx="16"
      fill="#FFFFFF"
      stroke="#CBD2E0"
      strokeWidth="1.5"
    />
    <rect x="136" y="82" width="60" height="44" rx="8" fill="#EEF2FA" />
    <rect x="208" y="82" width="60" height="44" rx="8" fill="#EEF2FA" />
    <rect x="280" y="82" width="60" height="44" rx="8" fill="#EEF2FA" />
    <g
      fill="none"
      stroke="#1E3A78"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M156 96h20M156 104h12M156 112h16" />
      <path d="M228 96h20M228 104h12M228 112h16" />
      <path d="M300 96h20M300 104h12M300 112h16" />
    </g>
    <rect x="136" y="144" width="150" height="10" rx="5" fill="#EEF2FA" />
    <rect x="136" y="164" width="90" height="10" rx="5" fill="#EEF2FA" />
    <rect x="252" y="160" width="88" height="22" rx="6" fill="#14295A" />
    <circle
      cx="366"
      cy="60"
      r="32"
      fill="#1B7A5A"
      stroke="#FFFFFF"
      strokeWidth="6"
    />
    <path
      d="M352 61l9 9 19-19"
      stroke="#FFFFFF"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Illustration>
);
